import React from 'react';

import { getProjectBrief, getProjectBriefVariables } from '__generated__/getProjectBrief';
import { ProjectBriefFields } from '__generated__/ProjectBriefFields';
import { Currency } from 'common/utils/currency';
import FilterDescription from 'projects/components/FilterDescription';
import Brief from 'projects/routes/Brief';
import BriefQuery from 'projects/routes/BriefQuery';
import InviteDesignersPage from 'projects/routes/InviteDesignersPage';
import { Industries } from 'projects/util/industries';
import { BriefStep, StepState } from 'projects/util/step';
import { RouteComponentProps, withRouter } from 'react-router';
import { Route, Switch } from 'react-router-dom';
import AboutYouPage from './AboutYouPage';
import DetailsPage from './DetailsPage';
import queries from './ProjectBrief.graphql';
import SummaryPage from './SummaryPage';
import TimeAndBudgetPage from './TimeAndBudgetPage';

type Props = RouteComponentProps<{
    briefId: string;
    step: string;
}>;

const steps: BriefStep<ProjectBriefFields>[] = [
    {
        step: 1,
        backgroundImage: require('images/projects/create/Step1.svg'),
        title: __('Tell us about your project'),
        fields: [
            'language',
            'title',
            'category',
            'description',
            'usage',
            'referenceFiles',
            'inspirationFiles',
            'inspirationUrls',
        ],
    },
    {
        step: 2,
        backgroundImage: require('images/projects/create/Step2.svg'),
        title: __('Your time & budget'),
        fields: ['startDate', 'endDate', 'timeframe', 'budget'],
    },
    {
        step: 3,
        backgroundImage: require('images/projects/create/Step3.svg'),
        title: __('About you'),
        fields: [
            'industry',
            'urls',
            'agencyType',
            'referralType',
            'referral',
            'profileApproval',
            'other',
        ],
    },
    { step: 4, title: __('Review your brief') },
    {
        step: 5,
        title: __('Invite designers to quote'),
        isAuthRequired: true,
        isDesignerStep: true,
        nextActionLabel: __('Confirm designers'),
    },
];

export const ProjectBrief: React.FunctionComponent<Props> = props => (
    <BriefQuery<getProjectBrief, getProjectBriefVariables>
        query={queries.getProjectBrief}
        variables={{ id: props.match.params.briefId }}
        fieldsTypename="ProjectBriefFields"
    >
        {(data, refetch) => {
            const step = new StepState({
                steps,
                brief: data.brief,
                currentStep: parseInt(props.match.params.step, 10),
            });

            const brief = {
                ...data.brief,
                fields: data.brief.fields as ProjectBriefFields,
            };

            const industries = new Industries(data.industries);
            const siteCurrency = data.site.currency as Currency;

            return (
                <Brief
                    refetch={refetch}
                    schema="project"
                    brief={brief}
                    viewer={data.viewer}
                    stepState={step}
                >
                    {form => (
                        <Switch>
                            <Route
                                path={`/briefs/projects/:briefId/1`}
                                render={() => (
                                    <DetailsPage
                                        form={form}
                                        brief={brief}
                                        categories={data.categories}
                                    />
                                )}
                            />
                            <Route
                                path={`/briefs/projects/:briefId/2`}
                                render={() => (
                                    <TimeAndBudgetPage
                                        form={form}
                                        brief={brief}
                                        siteCurrency={siteCurrency}
                                    />
                                )}
                            />
                            <Route
                                path={`/briefs/projects/:briefId/3`}
                                render={() => (
                                    <AboutYouPage
                                        form={form}
                                        brief={brief}
                                        viewer={data.viewer}
                                        industries={data.industries}
                                    />
                                )}
                            />
                            <Route
                                path={`/briefs/projects/:briefId/4`}
                                render={() => <SummaryPage brief={brief} industries={industries} />}
                            />
                            <Route
                                path={`/briefs/projects/:briefId/5`}
                                render={() => (
                                    <InviteDesignersPage
                                        brief={brief}
                                        viewer={data.viewer}
                                        description={
                                            <FilterDescription
                                                industries={data.industries}
                                                brief={brief}
                                                category={brief.category! && brief.category!.title}
                                                budget={brief.fields.budget}
                                            />
                                        }
                                    />
                                )}
                            />
                        </Switch>
                    )}
                </Brief>
            );
        }}
    </BriefQuery>
);

export default withRouter(ProjectBrief);
